/**
 * subscriptionCache — Persists subscription verification across WebView reloads.
 *
 * On iOS, opening the native camera can cause the WebView process to be killed
 * by the OS. When the user returns, the entire app reloads from scratch,
 * triggering a full subscription verification cycle (auth restore → backend call
 * → loading screen → page reload).
 *
 * This module caches the last successful subscription verification in
 * localStorage with a 24-hour TTL. The subscription is verified ONCE at
 * login and then served from cache for the rest of the session. The cache
 * is cleared on sign-out, so the next login always performs a fresh check.
 *
 * SECURITY: This is a performance cache, NOT a security boundary.
 * The backend is still the authoritative source. If the cache is stale or
 * tampered with, the next full verification will correct it. Explicit
 * revocations from the backend always overwrite the cache.
 */

const CACHE_KEY = "tm_sub_cache";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours — subscription is verified once at login

/**
 * Subscription verification states (ordered by trust level):
 *   "verified"              — backend confirmed access within TTL
 *   "temporarily_unverifiable" — backend unreachable, last-known-good still within TTL
 *   "stale"                 — TTL expired, needs fresh verification
 *   "denied"                — backend explicitly returned access_allowed=false
 */
export type SubscriptionCacheState = "verified" | "temporarily_unverifiable" | "stale" | "denied";

export interface CachedSubscription {
  accessAllowed: boolean;
  tier: string | null;
  jobLimit: number | null;
  aiEnabled: boolean;
  status: string | null;
  provider: string | null;
  renewalDate: string | null;
  graceAccess: boolean;
  accessSource: string;
  role: string;
  cachedAt: number;
  userId: string;
  /** How the cache entry was last resolved */
  cacheState: SubscriptionCacheState;
}

/**
 * Get the cache state for diagnostic/grace-period decisions.
 */
export function getCacheState(userId: string): SubscriptionCacheState {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return "stale";
    const cached: CachedSubscription = JSON.parse(raw);
    if (cached.userId !== userId) return "stale";
    if (!cached.accessAllowed) return "denied";
    if (Date.now() - cached.cachedAt > CACHE_TTL_MS) return "stale";
    return cached.cacheState ?? "verified";
  } catch {
    return "stale";
  }
}

export function getCachedSubscription(userId: string): CachedSubscription | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const cached: CachedSubscription = JSON.parse(raw);

    // Must be same user
    if (cached.userId !== userId) return null;

    // Must be within TTL
    if (Date.now() - cached.cachedAt > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    // Only cache positive access — never cache denial
    if (!cached.accessAllowed) return null;

    // CRITICAL: If the subscription has a renewal/expiry date and it's in the past,
    // do NOT trust the cache — force a fresh backend check.
    // This applies even when status still says "active", because a stale client cache
    // must never outlive the server-side renewal boundary.
    if (cached.renewalDate) {
      const renewalTime = new Date(cached.renewalDate).getTime();
      if (renewalTime <= Date.now()) {
        console.warn("[subscriptionCache] Cached subscription has passed renewal date — invalidating cache");
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
    }

    return cached;
  } catch {
    return null;
  }
}

export function setCachedSubscription(userId: string, data: Omit<CachedSubscription, "cachedAt" | "userId" | "cacheState"> & { cacheState?: SubscriptionCacheState }): void {
  try {
    const entry: CachedSubscription = {
      ...data,
      userId,
      cachedAt: Date.now(),
      cacheState: data.cacheState ?? "verified",
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage unavailable — no-op
  }
}

/**
 * Mark an existing cache entry as "temporarily_unverifiable" without
 * changing the cached access data. Used when backend is unreachable
 * but we want to preserve last-known-good access.
 */
export function markCacheTemporarilyUnverifiable(): void {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return;
    const cached: CachedSubscription = JSON.parse(raw);
    cached.cacheState = "temporarily_unverifiable";
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {
    // no-op
  }
}

export function clearCachedSubscription(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // no-op
  }
}
